import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Space, 
  Tag, 
  Divider, 
  Image, 
  Badge, 
  Progress, 
  Statistic,
  Alert,
  Spin,
  Timeline,
  List,
  Avatar,
  Rate,
  Modal,
  Input,
  Form,
  message,
  Carousel,
  Tabs
} from 'antd';
import { 
  ShoppingCartOutlined, 
  HeartOutlined, 
  ShareAltOutlined, 
  StarOutlined,
  FireOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CrownOutlined,
  TrophyOutlined,
  RocketOutlined,
  TeamOutlined,
  GlobalOutlined,
  TruckOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons';
import { publicProductService } from '../services/publicApi';
import './BrandPromotion.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const BrandPromotion = () => {
  const { brandName } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [contactForm] = Form.useForm();

  // Configuration des marques
  const brandConfig = {
    'schneider': {
      name: 'Schneider Electric',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Schneider_Electric_logo.svg/200px-Schneider_Electric_logo.svg.png',
      color: '#1890ff',
      description: 'Leader mondial de la gestion d\'énergie et de l\'automatisation',
      slogan: 'Life Is On',
      features: [
        'Innovation technologique de pointe',
        'Solutions durables et écologiques',
        'Support technique 24/7',
        'Garantie étendue 5 ans'
      ]
    },
    'legrand': {
      name: 'Legrand',
      logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Legrand_logo.svg/200px-Legrand_logo.svg.png',
      color: '#52c41a',
      description: 'Expert en infrastructures électriques et numériques',
      slogan: 'Des solutions pour tous',
      features: [
        'Qualité française reconnue',
        'Design moderne et fonctionnel',
        'Installation professionnelle incluse',
        'Maintenance préventive gratuite'
      ]
    },
    'hager': {
      name: 'Hager',
      logo: '/images/hager2.png',
      color: '#722ed1',
      description: 'Spécialiste des solutions électriques et de câblage',
      slogan: 'L\'énergie de l\'innovation',
      features: [
        'Technologie allemande de précision',
        'Économies d\'énergie garanties',
        'Formation des équipes incluse',
        'Pièces de rechange disponibles'
      ]
    }
  };

  const currentBrand = brandConfig[brandName] || brandConfig['schneider'];

  // Gestion d'erreur globale
  if (!currentBrand) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column'
      }}>
        <Alert
          message="Marque non trouvée"
          description="La marque demandée n'existe pas dans notre catalogue."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Button onClick={() => navigate('/')}>
          Retour à l'accueil
        </Button>
      </div>
    );
  }

  useEffect(() => {
    loadBrandProducts();
  }, [brandName]);

  const loadBrandProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simuler des produits de la marque (en attendant l'API)
      const mockProducts = [
        {
          _id: '1',
          name: `${currentBrand.name} - Système Intelligent`,
          reference: `${brandName.toUpperCase()}-001`,
          description: 'Solution complète d\'automatisation et de gestion d\'énergie',
          pricing: { publicPrice: 2500, currency: 'EUR' },
          images: { main: '' },
          features: ['Contrôle à distance', 'Économies d\'énergie', 'Installation facile'],
          isPromo: true,
          discount: 25
        },
        {
          _id: '2',
          name: `${currentBrand.name} - Détecteur Premium`,
          reference: `${brandName.toUpperCase()}-002`,
          description: 'Détecteur de haute technologie avec connectivité avancée',
          pricing: { publicPrice: 450, currency: 'EUR' },
          images: { main: '' },
          features: ['Détection précise', 'Alarme silencieuse', 'Batterie longue durée'],
          isPromo: true,
          discount: 30
        },
        {
          _id: '3',
          name: `${currentBrand.name} - Centrale Modulaire`,
          reference: `${brandName.toUpperCase()}-003`,
          description: 'Centrale de contrôle modulaire et évolutive',
          pricing: { publicPrice: 1800, currency: 'EUR' },
          images: { main: '' },
          features: ['Modularité', 'Évolutivité', 'Interface intuitive'],
          isPromo: true,
          discount: 20
        }
      ];
      
      setProducts(mockProducts);
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      setError('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const normalizeImageUrl = (raw) => {
    if (!raw) return '';
    let url = String(raw);
    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    if (url.startsWith('/uploads')) return `${url}`;
    return `${url}`;
  };

  const handleContactSubmit = async (values) => {
    try {
      console.log('Message de contact:', values);
      message.success('Votre demande a été envoyée avec succès !');
      setContactModalVisible(false);
      contactForm.resetFields();
    } catch (error) {
      message.error('Erreur lors de l\'envoi du message');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Promotion ${currentBrand.name}`,
        text: `Découvrez les offres promotionnelles de ${currentBrand.name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      message.success('Lien copié dans le presse-papiers !');
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '50px 20px', textAlign: 'center' }}>
        <Alert
          message="Erreur"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => navigate('/produits')}>
              Voir tous les produits
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="brand-promotion-page">
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 20px' }}>
        
        {/* En-tête de la marque */}
        <Card className="brand-header-card">
          <Row gutter={[32, 32]} align="middle">
            <Col xs={24} md={8}>
              <div style={{ textAlign: 'center' }}>
                <img 
                  src={currentBrand.logo} 
                  alt={currentBrand.name}
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '100px', 
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                  }}
                />
              </div>
            </Col>
            <Col xs={24} md={16}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Badge.Ribbon text="PROMOTION EXCLUSIVE" color="red">
                    <Title level={1} className="brand-title" style={{ color: currentBrand.color }}>
                      {currentBrand.name}
                    </Title>
                  </Badge.Ribbon>
                  <Title level={3} style={{ color: currentBrand.color, marginTop: 8 }}>
                    {currentBrand.slogan}
                  </Title>
                </div>
                
                <Paragraph style={{ fontSize: 18, marginBottom: 0 }}>
                  {currentBrand.description}
                </Paragraph>

                <Space wrap>
                  <Tag className="brand-tag" color={currentBrand.color} icon={<CrownOutlined />}>
                    Marque Premium
                  </Tag>
                  <Tag className="brand-tag" color="green" icon={<GiftOutlined />}>
                    Jusqu'à -30%
                  </Tag>
                  <Tag className="brand-tag" color="blue" icon={<ClockCircleOutlined />}>
                    Offre limitée
                  </Tag>
                </Space>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Section Promotion - Slider + Détails */}
        <Card className="promotion-section-card" style={{ marginTop: 24 }}>
          <Row gutter={[32, 32]} align="stretch">
            {/* Colonne gauche - Slider */}
            <Col xs={24} lg={14}>
              <div className="promotion-slider-container">
                <Carousel
                  autoplay
                  autoplaySpeed={4000}
                  dots={{ className: 'custom-dots' }}
                  effect="fade"
                  infinite
                  speed={800}
                  style={{ 
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }}
                >
                  {currentBrand.promotionImages?.map((image, index) => (
                    <div key={index}>
                      <div
                        style={{
                          height: 400,
                          backgroundImage: `url(${image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}
                      >
                        {/* Overlay avec gradient */}
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, rgba(0,0,0,0.3), rgba(0,0,0,0.1))'
                          }}
                        />
                        
                        {/* Contenu overlay */}
                        <div style={{ 
                          position: 'relative', 
                          zIndex: 2, 
                          textAlign: 'center',
                          color: 'white',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                        }}>
                          <Title level={2} style={{ color: 'white', marginBottom: 16 }}>
                            Promotion {currentBrand.name}
                          </Title>
                          <Paragraph style={{ 
                            fontSize: 18, 
                            color: 'white',
                            maxWidth: 500,
                            margin: '0 auto'
                          }}>
                            Découvrez nos offres exceptionnelles et nos produits de qualité professionnelle
                          </Paragraph>
                        </div>
                      </div>
                    </div>
                  )) || (
                    // Images par défaut si pas d'images de promotion
                    [
                      {
                        url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
                        title: 'Solutions Professionnelles',
                        description: 'Équipements de haute qualité pour vos projets'
                      },
                      {
                        url: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
                        title: 'Innovation Technologique',
                        description: 'Les dernières technologies au service de votre efficacité'
                      },
                      {
                        url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
                        title: 'Support Expert',
                        description: 'Accompagnement personnalisé par nos spécialistes'
                      }
                    ].map((slide, index) => (
                      <div key={index}>
                        <div
                          style={{
                            height: 400,
                            backgroundImage: `url(${slide.url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(45deg, rgba(0,0,0,0.4), rgba(0,0,0,0.2))'
                            }}
                          />
                          
                          <div style={{ 
                            position: 'relative', 
                            zIndex: 2, 
                            textAlign: 'center',
                            color: 'white',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                          }}>
                            <Title level={2} style={{ color: 'white', marginBottom: 16 }}>
                              {slide.title}
                            </Title>
                            <Paragraph style={{ 
                              fontSize: 18, 
                              color: 'white',
                              maxWidth: 500,
                              margin: '0 auto'
                            }}>
                              {slide.description}
                            </Paragraph>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </Carousel>
              </div>
            </Col>

            {/* Colonne droite - Détails de l'offre */}
            <Col xs={24} lg={10}>
              <div className="promotion-details-container">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  
                  {/* Header avec badge */}
       

           

                  {/* Avantages en grille */}
                  <div>
                    <Title level={4} style={{ color: currentBrand.color, marginBottom: 16, textAlign: 'center' }}>
                      Pourquoi choisir cette offre ?
                    </Title>
                    <Row gutter={[12, 12]}>
                      {[
                        { icon: <FireOutlined />, text: 'Prix réduits', color: '#ff4d4f' },
                        { icon: <TruckOutlined />, text: 'Livraison offerte', color: '#52c41a' },
                        { icon: <SafetyCertificateOutlined />, text: 'Garantie 3 ans', color: '#1890ff' },
                        { icon: <CustomerServiceOutlined />, text: 'Support premium', color: '#722ed1' }
                      ].map((item, index) => (
                        <Col span={12} key={index}>
                          <div style={{
                            background: 'white',
                            padding: '16px',
                            borderRadius: '12px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: `2px solid ${item.color}20`,
                            transition: 'all 0.3s ease'
                          }}>
                            <div style={{ color: item.color, fontSize: 24, marginBottom: 8 }}>
                              {item.icon}
                            </div>
                            <Text style={{ fontSize: 14, fontWeight: 600 }}>{item.text}</Text>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>

                  {/* Footer info */}
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px',
                    background: `${currentBrand.color}10`,
                    borderRadius: '12px',
                    border: `1px dashed ${currentBrand.color}40`
                  }}>
                    <ClockCircleOutlined style={{ color: currentBrand.color, marginRight: 8 }} />
                    <Text style={{ fontSize: 13, color: '#666' }}>
                      Offre valable jusqu'au 31/12/2024 • Commande minimum 500€ HT
                    </Text>
                  </div>

                </Space>
              </div>
            </Col>
          </Row>
        </Card>

      </div>
    </div>
  );
};

export default BrandPromotion;
